// This controller borrows from toggle_controller.js and does some additional stuff specific to
// multi-select.

import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "experienceStep",
    "payerTypeStep",
    "engagementStep",
    "completedClaimStep",
    "completedClaimStepB",
    "addClaimsStep",
    "finishStep",
    "refreshFinishStepButton",
    "charityCareReviewType",
    "initialEngagementOwner", // "partner" or "goodbill"
    "completedClaimMethod",
    "exportSecureEmailAddress",
    "files",
    "tbody",
    "uploadForm",
    "blobs",
    "uploadRowTemplate",
    "finishForm"
  ];

  connect() {
    this.gotDraftClaims = this.tbodyTarget.querySelectorAll("tr").length > 0;

    this.refreshSelected();

    // Set up click-to-select files.
    this.uploadFormTarget.addEventListener("click", this.pickFiles.bind(this));

    // Set up drag-n-drop.
    // Previously we tried Dropzone, but it comes with more features/baggage than we need.
    // For example, it wants to automatically show preview tiles for the files, to perform
    // the upload, show progress, etc.  And it caused some issues with the click-to-select
    // behavior.  So, we reverted to using a simple home-grown solution.
    // https://www.smashingmagazine.com/2018/01/drag-drop-file-uploader-vanilla-js/
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.uploadFormTarget.addEventListener(eventName, this.dragHandler.bind(this), false);
    });

    // Detect when there are any changes in the table body so we can refresh finishStep accordingly.
    this.observer = new MutationObserver(this.refreshFinishStep.bind(this));
    this.observer.observe(this.tbodyTarget, { childList: true, subtree: true, attributes: true });
  }

  refreshSelected() {
    this.setExperienceSelected();
    this.setPayerTypeSelected();
    this.setEngagementSelected();
    this.setExportMethodSelected();

    if (this.experienceSelected) {
      this.onExperienceSelected();
    }
  }

  setExperienceSelected() {
    // User needs to pick both options.
    const selected501rOption = this.experienceStepTarget.querySelector("[name=charity_care_review_type]:checked")
    const selectedClaimReviewOption = this.experienceStepTarget.querySelector("[name=claim_review_type]:checked")
    this.experienceSelected = selected501rOption && selectedClaimReviewOption;
  }

  setPayerTypeSelected() {
    this.payerTypeSelected = false;
    var selectedPayerType = this.payerTypeStepTarget.querySelector("[type=radio]:checked");
    if (selectedPayerType) {
      this.payerTypeSelected = true;
    }
  }

  setEngagementSelected() {
    this.engagementSelected = false;
    var selectedEngagement = this.engagementStepTarget.querySelector("[type=radio]:checked");
    if (selectedEngagement) {
      this.engagementSelected = true;
    }
  }

  setExportMethodSelected() {
    this.exportMethodSelected = false;
    var selectedExportMethod = this.completedClaimStepTarget.querySelector("[type=radio]:checked");
    if (selectedExportMethod) {
      this.exportMethodSelected = true;
    }
  }

  onExperienceSelected(e) {
    // Make sure we have values for both 501(r) and claim review.
    this.setExperienceSelected();
    if (!this.experienceSelected) {
      return;
    }

    this.payerTypeStepTarget.classList.remove("hidden");
    if (e) {
      e.target.form.requestSubmit();
    }

    if (this.payerTypeSelected) {
      this.onPayerTypeSelected();
    }
  }

  onPayerTypeSelected(e) {
    this.payerTypeSelected = true;

    this.engagementStepTarget.classList.remove("hidden");
    if (e) {
      e.target.form.requestSubmit();
    }

    if (this.engagementSelected) {
      this.onEngagementSelected();
    }
  }

  onEngagementSelected(e) {
    this.engagementSelected = true;

    this.completedClaimStepTarget.classList.remove("hidden");
    if (e) {
      e.target.form.requestSubmit();
    }

    if (this.exportMethodSelected) {
      this.onCompletedClaimSelected();
    }
  }

  onCompletedClaimSelected(e) {
    if (this.completedClaimStepTarget.querySelector("[type=radio]:checked").value === "email") {
      this.completedClaimStepBTarget.classList.remove("hidden");

      this.gotDraftClaims = this.tbodyTarget.querySelectorAll("tr").length > 0;
      this.gotEmail = this.exportSecureEmailAddressTarget.value && this.exportSecureEmailAddressTarget.value.length > 0;

      if (!this.gotEmail && !this.gotDraftClaims) {
        this.addClaimsStepTarget.classList.add("hidden");
      } else {
        this.addClaimsStepTarget.classList.remove("hidden");
      }
    } else {
      this.completedClaimStepBTarget.classList.add("hidden");
      this.addClaimsStepTarget.classList.remove("hidden");
    }

    if (e) {
      e.target.form.requestSubmit();
    }
  }

  onExportSecureEmailAddressChanged(e) {
    var value = this.completedClaimStepBTarget.querySelector("input").value;
    if (value && value.length > 0) {
      this.addClaimsStepTarget.classList.remove("hidden");
    } else {
      this.gotDraftClaims = this.tbodyTarget.querySelectorAll("tr").length > 0;
      if (!this.gotDraftClaims) {
        this.addClaimsStepTarget.classList.add("hidden");
      }
    }

    if (this.submitTimer) {
      // console.log("clearing");
      clearTimeout(this.submitTimer);
    }

    // console.log("setting timeout");
    this.submitTimer = setTimeout(() => {
      // console.log("submitting");
      e.target.form.requestSubmit();
      this.submitTimer = null;
    }, 1000);
  }

  submitFinish(e) {
    // Make sure the form doesn't submit the normal way, or we'll have a double-submission!
    e.preventDefault();

    // Submit the form
    // console.log("Submitting finish form at time: " + new Date().toLocaleTimeString());
    this.finishFormTarget.submit();
  }

  dragHandler(e) {
    e.preventDefault();
    e.stopPropagation();

    if (['dragenter', 'dragover'].includes(e.type)) {
      this.uploadFormTarget.classList.add("bg-light-gray");
    } else {
      this.uploadFormTarget.classList.remove("bg-light-gray");
    }

    if (e.type === 'drop') {
      this.uploadFiles(e.dataTransfer.files);
    }
  }

  pickFiles(e) {
    this.filesTarget.click();
  }

  filesPicked(e) {
    this.uploadFiles(e.target.files);
  }

  uploadFiles(files) {
    var numUploaders = 0;
    var numFinished = 0;

    // Clear out the blob-metadata for any files we uploaded before.
    this.blobsTarget.innerHTML = "";

    for (var i = 0; i < files.length; i++) {
      const file = files[i];

      // Create a row in the table for this file to show progress as it's uploaded.
      const row = this.uploadRowTemplateTarget.cloneNode(true);
      row.classList.remove("hidden");
      row.querySelector("[data-filename]").textContent = file.name;
      row.id = file.name;
      this.tbodyTarget.append(row);
      const progressNode = row.querySelector("[data-progress]");

      var uploader = new Uploader(file, progressNode, this.blobsTarget);

      numUploaders++;

      uploader.upload((success, error) => {
        // if (success) {
        //   console.log("Upload success for " + file.name);
        // } else {
        //   console.log("Upload failed for " + file.name + ": " + error);
        // }

        // console.log("numFinished: " + numFinished + ", numUploaders: " + numUploaders)

        numFinished++;

        // If all uploads are finished, submit the form.
        if (numFinished == numUploaders) {
          // console.log("Submitting form...");

          const fetchParams = {
            method: this.uploadFormTarget.method,
            body: new FormData(this.uploadFormTarget),
            headers: {
              Accept: "text/vnd.turbo-stream.html"
            }
          };

          fetch(this.uploadFormTarget.action, fetchParams)
            .then(response => response.text())
            .then((html) => Turbo.renderStreamMessage(html));
        }
      });
    }
  }

  refreshFinishStep(mutationsList, observer) {
    var overallStatusChanged = false;
    mutationsList.forEach((mutation) => {
      const combinedList = [...mutation.addedNodes, ...mutation.removedNodes];
      combinedList.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.hasAttribute('data-upload-claim-status') || node.querySelector('[data-upload-claim-status]')) {
            overallStatusChanged = true;
          }
        }
      });
    });

    if (overallStatusChanged) {
      // console.log("Refreshing finishStep...");

      var form = this.refreshFinishStepButtonTarget.form;
      const fetchParams = {
        method: form.method,
        headers: {
          Accept: "text/vnd.turbo-stream.html"
        }
      };

      fetch(form.action, fetchParams)
        .then(response => response.text())
        .then((html) => Turbo.renderStreamMessage(html));
    } else {
      // console.log("No overall status change");
    }
  }
}

class Uploader {
  constructor(file, progressNode, blobsNode) {
    this.file = file;
    this.progressNode = progressNode;
    this.blobsNode = blobsNode;

    this.directUpload = new DirectUpload(this.file, "/rails/active_storage/direct_uploads", this);

    this.finished = false;
    this.error = null;
    this.blob = null;
  }

  upload(callback) {
    this.directUpload.create((error, blob) => {
      this.finished = true;
      this.error = error;
      this.blob = blob;

      if (error) {
        callback(false, error);
      } else {
        // Add the blob info to the form
        const hiddenField = document.createElement('input')
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("value", blob.signed_id);
        hiddenField.name = "files[]"
        this.blobsNode.appendChild(hiddenField)

        // Use the with blob.signed_id as a file reference in next request
        // console.log(`Finished uploading ${blob.filename}, now stored under key ${blob.key}`);
        callback(true);
      }
    })
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress",
      event => this.directUploadDidProgress(event))
  }

  directUploadDidProgress(event) {
    // Use event.loaded and event.total to update the progress bar
    const fractionDone = event.loaded / event.total;
    // console.log(`Direct upload for file ${this.file.name} is ${fractionDone} done`);
    this.progressNode.innerText = Math.round(fractionDone * 100) + "% uploaded";
  }
}